import styled from 'styled-components';
import { Button, Stack, Text } from '@mantine/core';
import { DX, DX_ROOT_CLASS } from '../../theme/v2';
import { V2Theme } from '../../theme/v2/V2Theme';

/**
 * The parts every V2 page has been building by hand.
 *
 * Extraction, transformation, report and the project list each wrote their own toolbar strip, their
 * own rail, their own 11/700 caption and their own empty state — the same shapes, a few pixels
 * apart. These are those shapes, once, on the tokens.
 *
 * `Workspace` is also where a page opts into V2: it carries the `dx-v2` class the tokens are scoped
 * to and the nested Mantine theme, so nothing here can reach a legacy screen.
 */

const Shell = styled.div`
  height: ${({ $height }) => $height};
  display: flex;
  flex-direction: column;
  background: ${DX.surface};
  color: ${DX.text};
`;

/**
 * The page.
 *
 * It fills the scroll container the app chrome leaves it. It used to measure the chrome itself —
 * `calc(100vh - 68px)` — and the chrome has not been 68px for a while: the workspace ended twenty
 * pixels above the window, so the foot of the list floated with a strip of page under it. A height
 * that subtracts a constant is a height that is wrong the next time a bar changes.
 */
export const Workspace = ({ children, height = '100%', className = '', ...rest }) => (
  <V2Theme>
    <Shell
      $height={height}
      className={className ? `${DX_ROOT_CLASS} ${className}` : DX_ROOT_CLASS}
      {...rest}
    >
      {children}
    </Shell>
  </V2Theme>
);

/** A strip of controls under the chrome: search, filters, and the one primary action. */
export const Toolbar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${DX.space[3]};
  min-height: ${DX.bar.toolbar};
  padding: 0 ${DX.space[5]};
  border-bottom: ${DX.hairline};
`;

/** Pushes what follows it to the right of a Toolbar or a StatusBar. */
export const Spacer = styled.span`
  margin-left: auto;
`;

/** Rail beside canvas. */
export const Body = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
`;

/** What you can look at: views, filters, sections. A list you can read without opening it. */
export const Rail = styled.nav`
  flex: 0 0 ${({ $width = 216 }) => `${$width}px`};
  min-width: 0;
  padding: ${DX.space[4]} ${DX.space[3]};
  border-right: ${DX.hairline};
  overflow-y: auto;
`;

export const Canvas = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/** 11/700 uppercase, tracked, muted. The label above a group of anything. */
export const Caption = styled.div`
  padding: ${({ $bare }) => ($bare ? '0' : `0 ${DX.space[3]} ${DX.space[2]}`)};
  ${DX.caption}
`;

/** A row in the rail. `data-active` marks the one you are looking at. */
export const RailItem = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${DX.space[3]};
  width: 100%;
  padding: ${DX.space[2]} ${DX.space[3]};
  border: none;
  border-radius: ${DX.radius.item};
  background: none;
  font-size: ${DX.fz.sm};
  line-height: ${DX.lh.sm};
  text-align: left;
  color: ${DX.text};
  cursor: pointer;
  transition: background-color ${DX.duration} ${DX.ease};

  &:hover {
    background: ${DX.hover};
  }

  &[data-active='true'] {
    background: ${DX.active};
    font-weight: ${DX.fw.strong};
  }

  .count {
    color: ${DX.textMuted};
  }
`;

/** The foot of the page: how much there is, and what a selection can do with it. */
export const StatusBar = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: ${DX.space[4]};
  height: ${DX.bar.status};
  padding: 0 ${DX.space[5]};
  border-top: ${DX.hairline};
  font-size: ${DX.fz.xs};
  color: ${DX.textMuted};
`;

/** A bounded surface inside the canvas: one hairline, radius 8, no shadow. */
export const Panel = styled.div`
  border: ${DX.hairline};
  border-radius: ${DX.radius.card};
  background: ${DX.surface};
  overflow: hidden;
`;

/**
 * A value that carries a state.
 *
 * `done` is the same black as the page on purpose — on a dense surface a finished state that shouts
 * is noise. Colour is spent on what is next and on what has gone wrong.
 */
export const StatusText = styled.span`
  font-size: ${DX.fz.sm};
  line-height: ${DX.lh.sm};
  color: ${({ $tone }) => DX.tone[$tone] || DX.tone.done};
`;

/**
 * A list with nothing on it yet.
 *
 * The shape the app already uses — a 22px glyph, the name of what is missing, one line of what it
 * is for, and a solid button, because on an empty list it is the only way in. An empty state should
 * not be where a product changes its voice, so pages stop writing their own.
 */
export const EmptyState = ({ icon, title, description, action, children }) => (
  <Stack align="center" justify="center" gap={4} px="sm" style={{ flex: 1, paddingTop: 96 }}>
    {icon}

    <Text size="sm" fw={600} mt={4}>
      {title}
    </Text>

    {description && (
      <Text size="xs" c="dimmed" ta="center" style={{ maxWidth: 230, lineHeight: 1.45 }}>
        {description}
      </Text>
    )}

    {action && (
      <Button size="xs" variant="filled" mt={10} {...action.props}>
        {action.label}
      </Button>
    )}

    {children}
  </Stack>
);

/**
 * A toolbar control.
 *
 * The three that stood in the project list's toolbar came from three components — a Mantine input, a
 * filter button with its own inline colours, a Menu target with the theme's own idea of weight — and
 * the only thing one saw was that they disagreed. This is the shape they should all have been: 32px,
 * 13px regular, one hairline, one radius, the icon a shade quieter than the label.
 */
export const ControlButton = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  gap: ${DX.space[2]};
  height: ${DX.control.base};
  padding: 0 ${DX.space[4]};
  border: ${DX.hairline};
  border-radius: ${DX.radius.control};
  background: ${DX.surface};
  font-size: ${DX.fz.sm};
  font-weight: ${DX.fw.regular};
  line-height: ${DX.lh.sm};
  color: ${DX.text};
  cursor: pointer;
  white-space: nowrap;
  transition: background-color ${DX.duration} ${DX.ease};

  svg {
    flex-shrink: 0;
    color: ${DX.textFaint};
  }

  &:hover {
    background: ${DX.hover};
  }

  /* A filter that is doing something says so with its border, not with a second colour. */
  &[data-active='true'] {
    border-color: ${DX.accent};
  }

  &:disabled {
    color: ${DX.textFaint};
    cursor: default;
  }
`;
